import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  CompactSelection,
  DataEditor,
  GridCellKind,
  type DataEditorRef,
  type DrawCellCallback,
  type EditableGridCell,
  type GridCell,
  type GridColumn,
  type GridSelection,
  type Item,
  type Rectangle,
} from '@glideapps/glide-data-grid';
import '@glideapps/glide-data-grid/dist/index.css';
import { useLocation } from 'wouter';
import { gridTheme } from '../palette.js';
import { MenuSurface, type MenuItem } from '../ui/Menu.js';
import { paintFaintSuffix } from './cells/faintSuffix.js';
import { GRID_ICON_INSET, GRID_ICON_SIZE, paintSiteIcon, siteOf } from '../ui/SiteIcon.js';
import { CUSTOM_RENDERERS, TWO_LINE_ROW_HEIGHT, actionAt } from './cells/index.js';
import { nextSort, type GridColumnDef, type GridLink, type Sort } from './columns.js';
import type { GridPreferences } from './useGridPreferences.js';

/** The row height Glide uses when nothing says otherwise. */
const ONE_LINE_ROW_HEIGHT = 34;

/** How many rows short of the end the grid asks for the next page. */
const LOAD_AHEAD_ROWS = 50;

/**
 * The chevron column. It is not one of the page's columns: it holds nothing
 * about a row, so it has no business in a CSV, in the search, or in the column
 * order a person has arranged. It is frozen at the left instead, where the row
 * starts.
 */
const OPEN_COLUMN_ID = 'open';

const EMPTY_SELECTION: GridSelection = {
  columns: CompactSelection.empty(),
  rows: CompactSelection.empty(),
};

/** A row that opens into a docked pane: which one is open, and how to open another. */
export interface RowOpening<Row> {
  readonly openId: string | null;
  /** `columnId` is the column clicked, or null for the chevron, Enter or an arrow step. */
  readonly onOpen: (row: Row, columnId: string | null) => void;
  readonly onClose: () => void;
  /** Which columns a click opens the row from; every column without links or buttons when absent. */
  readonly opensFrom?: (columnId: string) => boolean;
  /** The chevron's hover text. The chevron column is drawn only where this is given. */
  readonly chevronTip?: string;
}

export interface DataGridProps<
  Row,
  Context,
  Column extends GridColumnDef<Row, Context> = GridColumnDef<Row, Context>,
> {
  rows: readonly Row[];
  /** In the order they are shown. */
  columns: readonly Column[];
  context: Context;
  rowId: (row: Row) => string;
  /** Widths and the row height, and the column order unless `onColumnMoved` says otherwise. */
  preferences: GridPreferences;
  /**
   * Absent: the headers do not sort (a feed the server orders, a list read in
   * its own order). `sortable` says which columns do, where the server does the
   * sorting; otherwise a column sorts if it has a `sortKey`.
   */
  sort?:
    | {
        value: Sort | null;
        onChange: (sort: Sort | null) => void;
        sortable?: (columnId: string) => boolean;
      }
    | undefined;
  /**
   * A dragged header, as indices into `columns`. Absent: the order is this
   * browser's preference. Null: the columns stay where they are.
   */
  onColumnMoved?: ((from: number, to: number) => void) | null | undefined;
  /** The column whose cells get the row's site icon painted at their left edge. */
  siteIcon?: { columnId: string; of: (row: Row) => { website: string | null; name: string } };
  open?: RowOpening<Row> | undefined;
  /** What a cell says on hover, beyond what it shows. A header says its column's `note`. */
  tip?: (row: Row, column: Column) => string | null;
  /** A last say over a cell, after its column painted it: a proposal's tint. */
  decorate?: (row: Row, column: Column, cell: GridCell) => GridCell;
  /** A row's commands, raised by a right-click or the context-menu key. */
  rowMenu?: { label: string; items: (row: Row) => readonly MenuItem[] } | undefined;
  /** A click on a button an actions cell drew. */
  onAction?: (row: Row, action: string) => void;
  /** Rows the server has not sent yet; the next page is asked for as the end nears. */
  paging?:
    | {
        hasMore: boolean;
        loading: boolean;
        onReachEnd: () => void;
        /** How many rows short of the end to ask, where not `LOAD_AHEAD_ROWS`. */
        loadAhead?: number;
      }
    | undefined;
  onCellEdited?: (row: Row, column: Column, value: EditableGridCell) => void;
  /** A pasted block, from the cell it starts at, in indices of `rows` and `columns`. */
  onPaste?: (
    start: { column: number; row: number },
    values: readonly (readonly string[])[],
  ) => void;
  /** Controlled where the page reads which rows are picked; the grid keeps its own otherwise. */
  selection?: { value: GridSelection; onChange: (selection: GridSelection) => void } | undefined;
  /** Checkboxes and row numbers both, and ticking a second row keeps the first. */
  multiSelect?: boolean;
  /** Dragging a row by its number. */
  onRowMoved?: ((from: number, to: number) => void) | undefined;
  /** Back to the top when this changes: a new sort or filter is a new list, read from its start. */
  scrollResetKey?: unknown;
  /** Columns held at the left as it scrolls, not counting the chevron. */
  freezeColumns?: number;
  /** What the grid says in place of rows when there are none. */
  empty?: React.ReactNode;
  /** The pane docked beside the grid, given the step the arrow keys take through the rows. */
  aside?: (step: (delta: 1 | -1) => void) => React.ReactNode;
}

type Raised =
  | { kind: 'row'; row: number; x: number; y: number }
  | { kind: 'links'; links: readonly GridLink[]; x: number; y: number };

/**
 * Every table in the app: the frame, the row height the density toggle sets,
 * widths and column order kept per browser, header sorting, the site icon
 * beside a name, hover explanations, a right-click menu, buttons in a cell,
 * links out of a cell, paging from the server as the end nears, and the
 * docked pane that follows the cursor up and down the rows.
 */
export function DataGrid<
  Row,
  Context,
  Column extends GridColumnDef<Row, Context> = GridColumnDef<Row, Context>,
>(props: DataGridProps<Row, Context, Column>): React.JSX.Element {
  const { rows, columns, context, preferences, open, siteIcon, rowId } = props;
  const grid = useRef<DataEditorRef>(null);
  const [, navigate] = useLocation();
  const [ownSelection, setOwnSelection] = useState<GridSelection>(EMPTY_SELECTION);
  const selection = props.selection?.value ?? ownSelection;
  const setSelection = props.selection?.onChange ?? setOwnSelection;
  const [raised, setRaised] = useState<Raised | null>(null);
  // Which cell or header the pointer is over, and where it is on the screen:
  // the grid is a canvas, so a hover affordance and its tooltip have nowhere
  // else to come from.
  const [hovered, setHovered] = useState<{
    kind: 'cell' | 'header';
    col: number;
    row: number;
    bounds: Rectangle;
  } | null>(null);

  const chevron = open?.chevronTip !== undefined;
  const offset = chevron ? 1 : 0;
  const columnAt = (col: number): Column | undefined => columns[col - offset];

  const sort = props.sort?.value ?? null;
  const gridColumns: GridColumn[] = useMemo(
    () => [
      ...(chevron ? [{ id: OPEN_COLUMN_ID, title: '', width: 34 }] : []),
      ...columns.map((column) => ({
        id: column.id,
        title:
          sort?.id === column.id
            ? `${column.title} ${sort.direction === 'asc' ? '▲' : '▼'}`
            : column.note === undefined
              ? column.title
              : `${column.title} ⓘ`,
        width: preferences.widthOf(column.id, column.width),
      })),
    ],
    [chevron, columns, preferences, sort],
  );

  const hoveredRow = hovered?.kind === 'cell' ? hovered.row : null;
  const { decorate } = props;
  const getCellContent = useCallback(
    ([col, rowIndex]: Item): GridCell => {
      const row = rows[rowIndex];
      if (row === undefined) return { kind: GridCellKind.Loading, allowOverlay: false };
      if (chevron && col === 0) return openCell(rowIndex === hoveredRow);
      const column = columns[col - offset];
      if (column === undefined) return { kind: GridCellKind.Loading, allowOverlay: false };
      const cell = column.cell(row, context);
      return decorate === undefined ? cell : decorate(row, column, cell);
    },
    [rows, columns, context, chevron, offset, hoveredRow, decorate],
  );

  const drawCell = useCallback<DrawCellCallback>(
    (args, draw) => {
      draw();
      const column = columns[args.col - offset];
      const row = rows[args.row];
      if (column === undefined || row === undefined) return;
      if (column.faintSuffix !== undefined)
        paintFaintSuffix(args, column.faintSuffix(row, context));
      if (siteIcon === undefined || column.id !== siteIcon.columnId) return;
      const { col, row: rowIndex, rect } = args;
      const { website, name } = siteIcon.of(row);
      paintSiteIcon(args.ctx, {
        site: siteOf(website),
        name,
        x: rect.x + GRID_ICON_INSET,
        y: rect.y + (rect.height - GRID_ICON_SIZE) / 2,
        size: GRID_ICON_SIZE,
        onReady: () => grid.current?.updateCells([{ cell: [col, rowIndex] }]),
      });
    },
    [siteIcon, columns, offset, rows, context],
  );

  /**
   * The pane follows the list rather than the list following the pane: the
   * cursor, the scroll and the pane all land on the same visible row, so
   * arrowing through the rows from either side means the same thing.
   */
  const openId = open?.openId ?? null;
  const onOpen = open?.onOpen;
  const step = useCallback(
    (delta: 1 | -1): void => {
      if (openId === null || onOpen === undefined) return;
      const index = rows.findIndex((row) => rowId(row) === openId);
      if (index === -1) return;
      const nextRow = index + delta;
      const next = rows[nextRow];
      if (next === undefined) return;
      onOpen(next, null);
      const col = selection.current?.cell[0] ?? offset;
      setSelection({
        columns: CompactSelection.empty(),
        rows: CompactSelection.empty(),
        current: {
          cell: [col, nextRow],
          range: { x: col, y: nextRow, width: 1, height: 1 },
          rangeStack: [],
        },
      });
      grid.current?.scrollTo(0, nextRow, 'vertical');
    },
    [openId, onOpen, rows, rowId, selection, setSelection, offset],
  );

  // Glide scrolls inside an element it does not hand out, so "near the end"
  // is read off the rows it shows rather than off a scroll position. It is a
  // condition, re-read whenever the visible rows move and whenever a page
  // lands, so a page arriving mid-scroll cannot lose the ask.
  const [visibleEnd, setVisibleEnd] = useState(0);
  const onVisibleRegionChanged = useCallback((range: Rectangle) => {
    setVisibleEnd(range.y + range.height);
  }, []);
  const hasMore = props.paging?.hasMore === true;
  const loadingMore = props.paging?.loading === true;
  const loadAhead = props.paging?.loadAhead ?? LOAD_AHEAD_ROWS;
  const reachEnd = useRef(props.paging?.onReachEnd);
  reachEnd.current = props.paging?.onReachEnd;
  useEffect(() => {
    if (!hasMore || loadingMore) return;
    if (visibleEnd >= rows.length - loadAhead) reachEnd.current?.();
  }, [hasMore, loadingMore, visibleEnd, rows.length, loadAhead]);

  const scrollResetKey = props.scrollResetKey;
  useEffect(() => {
    if (scrollResetKey !== undefined) grid.current?.scrollTo(0, 0, 'vertical');
  }, [scrollResetKey]);

  function follow(link: GridLink): void {
    if (link.href.startsWith('/')) navigate(link.href);
    else window.open(link.href, '_blank', 'noopener');
  }

  const tip = tipOf();
  function tipOf(): string | null {
    if (hovered === null) return null;
    if (chevron && hovered.col === 0) {
      return hovered.kind === 'cell' && rows[hovered.row] !== undefined
        ? (open?.chevronTip ?? null)
        : null;
    }
    const column = columnAt(hovered.col);
    if (column === undefined) return null;
    if (hovered.kind === 'header') return column.note ?? null;
    const row = rows[hovered.row];
    return row === undefined || props.tip === undefined ? null : props.tip(row, column);
  }

  const raisedRow = raised?.kind === 'row' ? rows[raised.row] : undefined;
  const menuItems: readonly MenuItem[] =
    raised?.kind === 'links'
      ? raised.links.map((link) => ({ label: link.label, onSelect: () => follow(link) }))
      : raisedRow !== undefined && props.rowMenu !== undefined
        ? props.rowMenu.items(raisedRow)
        : [];

  const moveColumn = props.onColumnMoved;

  return (
    <div className="work">
      <div className="grid-host">
        <DataEditor
          ref={grid}
          theme={gridTheme()}
          columns={gridColumns}
          rows={rows.length}
          rowHeight={
            preferences.density === 'comfortable' ? TWO_LINE_ROW_HEIGHT : ONE_LINE_ROW_HEIGHT
          }
          getCellContent={getCellContent}
          drawCell={drawCell}
          getCellsForSelection={true}
          customRenderers={CUSTOM_RENDERERS}
          rowMarkers={props.multiSelect === true ? 'both' : 'number'}
          {...(props.multiSelect === true
            ? // A checkbox column that unselects the last row when you tick the
              // next one is not a checkbox column.
              { rowSelect: 'multi' as const, rowSelectionMode: 'multi' as const }
            : {})}
          gridSelection={selection}
          onGridSelectionChange={setSelection}
          onVisibleRegionChanged={onVisibleRegionChanged}
          smoothScrollX
          smoothScrollY
          width="100%"
          height="100%"
          freezeColumns={offset + (props.freezeColumns ?? 0)}
          {...(props.onRowMoved === undefined ? {} : { onRowMoved: props.onRowMoved })}
          onCellEdited={([col, rowIndex], value) => {
            const column = columnAt(col);
            const row = rows[rowIndex];
            if (column === undefined || row === undefined) return;
            props.onCellEdited?.(row, column, value);
          }}
          onPaste={([col, rowIndex], values) => {
            if (props.onPaste === undefined || col < offset) return false;
            props.onPaste({ column: col - offset, row: rowIndex }, values);
            // The page writes the block itself, through the api, as one edit.
            return false;
          }}
          onHeaderClicked={(index) => {
            const column = columnAt(index);
            if (column === undefined || props.sort === undefined) return;
            const sortable = props.sort.sortable?.(column.id) ?? column.sortKey !== undefined;
            if (!sortable) return;
            props.sort.onChange(nextSort(sort, column.id));
          }}
          onColumnResize={(column, newSize) => {
            if (column.id !== undefined && column.id !== OPEN_COLUMN_ID) {
              preferences.setWidth(column.id, newSize);
            }
          }}
          onColumnMoved={(from, to) => {
            if (moveColumn === null || from < offset || to < offset) return;
            if (moveColumn === undefined) {
              preferences.move(
                columns.map((column) => column.id),
                from - offset,
                to - offset,
              );
            } else {
              moveColumn(from - offset, to - offset);
            }
          }}
          onItemHovered={(args) =>
            setHovered(
              args.kind === 'cell' || args.kind === 'header'
                ? {
                    kind: args.kind,
                    col: args.location[0],
                    row: args.location[1],
                    bounds: args.bounds,
                  }
                : null,
            )
          }
          onCellClicked={([col, rowIndex], event) => {
            const row = rows[rowIndex];
            if (row === undefined) return;
            if (chevron && col === 0) {
              open?.onOpen(row, null);
              return;
            }
            const column = columnAt(col);
            if (column === undefined) return;
            const action = actionAt(column.cell(row, context), event.localEventX);
            if (action !== null) {
              props.onAction?.(row, action.id);
              return;
            }
            const links = column.links?.(row, context) ?? [];
            const [only] = links;
            if (only !== undefined) {
              if (links.length === 1) follow(only);
              else {
                setRaised({
                  kind: 'links',
                  links,
                  x: event.bounds.x + event.localEventX,
                  y: event.bounds.y + event.localEventY,
                });
              }
              return;
            }
            if (open !== undefined && (open.opensFrom?.(column.id) ?? true)) {
              open.onOpen(row, column.id);
            }
          }}
          onCellContextMenu={([, rowIndex], event) => {
            if (props.rowMenu === undefined || rows[rowIndex] === undefined) return;
            event.preventDefault();
            setRaised({
              kind: 'row',
              row: rowIndex,
              x: event.bounds.x + event.localEventX,
              y: event.bounds.y + event.localEventY,
            });
          }}
          // The canvas has no key handler of its own to defer to, so the
          // gestures a grid does not already spend are claimed here.
          onKeyDown={(args) => {
            // While the pane is open the two vertical arrows belong to it: the
            // cursor and the pane move together, so the grid does not also
            // move the cursor on its own.
            if (openId !== null && (args.key === 'ArrowDown' || args.key === 'ArrowUp')) {
              args.cancel();
              step(args.key === 'ArrowDown' ? 1 : -1);
              return;
            }
            if (openId !== null && args.key === 'Escape') {
              args.cancel();
              open?.onClose();
              return;
            }

            const cell = selection.current?.cell;
            if (cell === undefined) return;
            const [col, rowIndex] = cell;
            const row = rows[rowIndex];
            if (row === undefined) return;

            if (
              open !== undefined &&
              args.key === 'Enter' &&
              !args.shiftKey &&
              !args.ctrlKey &&
              !args.metaKey
            ) {
              // Where a column edits on Enter, that is what Enter is for; from
              // anywhere else on the row it opens the row.
              if (columnAt(col)?.editsOnEnter === true) return;
              args.cancel();
              open.onOpen(row, null);
              return;
            }

            if (
              props.rowMenu !== undefined &&
              (args.key === 'ContextMenu' || (args.key === 'F10' && args.shiftKey))
            ) {
              args.cancel();
              const bounds = args.bounds;
              if (bounds === undefined) return;
              setRaised({
                kind: 'row',
                row: rowIndex,
                x: bounds.x + 8,
                y: bounds.y + bounds.height,
              });
            }
          }}
        />

        {rows.length === 0 && props.empty !== undefined && (
          <div className="grid-empty">{props.empty}</div>
        )}

        {tip !== null && hovered !== null && (
          <div
            className="grid-tip"
            role="tooltip"
            style={{ top: hovered.bounds.y + hovered.bounds.height + 4, left: hovered.bounds.x }}
          >
            {tip}
          </div>
        )}
      </div>

      {props.aside?.(step)}

      {raised !== null && menuItems.length > 0 && (
        <MenuSurface
          anchor={{ kind: 'point', x: raised.x, y: raised.y }}
          align="start"
          label={raised.kind === 'links' ? 'Links' : (props.rowMenu?.label ?? 'Row')}
          onClose={() => setRaised(null)}
          items={menuItems}
        />
      )}
    </div>
  );
}

/** Three ways in: the chevron, a click on the row, or Enter. */
function openCell(visible: boolean): GridCell {
  return {
    kind: GridCellKind.Text,
    data: '',
    displayData: visible ? '›' : '',
    allowOverlay: false,
    readonly: true,
  };
}
